import React, { useState, useEffect } from 'react';
import config from '../config';

export default function PfmeaSetupPage() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterProcess, setFilterProcess] = useState('');
    
    // UI State
    const [showForm, setShowForm] = useState(false);
    const [showLegend, setShowLegend] = useState(false);
    const [editingId, setEditingId] = useState(null); // Track which ID is being edited

    // Form State
    const initialFormState = {
        process_name: 'Laser',
        process_function: '',
        failure_mode: '',
        effect_of_failure: '',
        severity: 5,
        classification: '',
        potential_cause: '',
        occurrence: 3,
        current_controls: '',
        detection: 3
    };

    const [formData, setFormData] = useState(initialFormState);

    const PROCESS_OPTIONS = ["Material Intake", "Laser", "Brake", "Welding", "Drilling", "Tapping", "Blast", "Finish", "Assembly", "Shipping"];

    useEffect(() => {
        loadTemplates();
    }, [filterProcess]);

    const loadTemplates = async () => {
        setLoading(true);
        try {
            let url = `${config.API_BASE_URL}/pfmea/templates`;
            if (filterProcess) url += `?process_name=${filterProcess}`;
            const res = await fetch(url);
            const data = await res.json();
            setTemplates(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this template?")) return;
        await fetch(`${config.API_BASE_URL}/pfmea/templates/${id}`, { method: 'DELETE' });
        loadTemplates();
        // If we deleted the item currently being edited, close the form
        if (editingId === id) resetForm();
    };

    // Prepare form for editing
    const handleEdit = (template) => {
        setEditingId(template.pfmea_id);
        setFormData({
            process_name: template.process_name,
            process_function: template.process_function || '',
            failure_mode: template.failure_mode,
            effect_of_failure: template.effect_of_failure || '',
            severity: template.severity,
            classification: template.classification || '',
            potential_cause: template.potential_cause || '',
            occurrence: template.occurrence,
            current_controls: template.current_controls || '',
            detection: template.detection
        });
        setShowForm(true);
        // Optional: Scroll to top to show form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const resetForm = () => {
        setFormData(initialFormState);
        setEditingId(null);
        setShowForm(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                // UPDATE Existing
                const res = await fetch(`${config.API_BASE_URL}/pfmea/templates/${editingId}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData)
                });
                if (res.ok) {
                    resetForm(); // Close form on successful edit
                    loadTemplates();
                } else {
                    alert("Failed to update template.");
                }
            } else {
                // CREATE New
                const res = await fetch(`${config.API_BASE_URL}/pfmea/templates`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData)
                });
                if (res.ok) {
                    loadTemplates();
                    // Reset partial fields for rapid entry of similar items
                    setFormData({ 
                        ...formData, 
                        failure_mode: '', 
                        effect_of_failure: '',
                        potential_cause: '', 
                        current_controls: ''
                    }); 
                } else {
                    alert("Failed to create template.");
                }
            }
        } catch (err) {
            console.error(err);
            alert("Error saving template");
        }
    };

    const getRpnClass = (rpn) => {
        if (rpn >= 100) return "bg-danger text-white fw-bold";
        if (rpn >= 50) return "bg-warning text-dark";
        return "";
    };

    return (
        <div className="container-fluid p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>PFMEA Library Setup (Element 6)</h3>
                <div>
                    <button 
                        className="btn btn-outline-info me-2" 
                        onClick={() => setShowLegend(!showLegend)}
                    >
                        <i className="bi bi-info-circle me-1"></i> {showLegend ? "Hide Legend" : "Show Legend & Scoring Guide"}
                    </button>
                    <button className="btn btn-primary" onClick={() => { resetForm(); setShowForm(!showForm); }}>
                        {showForm && !editingId ? "Close Form" : "+ Add New Failure Mode"}
                    </button>
                </div>
            </div>

            {/* LEGEND / SCORING GUIDE */}
            {showLegend && (
                <div className="card mb-4 border-info shadow-sm">
                    <div className="card-header bg-info text-white">
                        <strong>PFMEA Legend & Scoring Guide (AIAG / VDA Standard)</strong>
                    </div>
                    <div className="card-body bg-light">
                        <div className="row">
                            <div className="col-md-4 border-end">
                                <h6 className="fw-bold text-decoration-underline">Scoring (1-10 Scale)</h6>
                                <ul className="list-unstyled small">
                                    <li className="mb-2">
                                        <span className="badge bg-danger me-1">S</span> <strong>Severity (1-10):</strong> How bad is the failure effect?
                                        <br/><span className="text-muted">10 = Safety Hazard / Regulatory. 1 = No discernible effect.</span>
                                    </li>
                                    <li className="mb-2">
                                        <span className="badge bg-warning text-dark me-1">O</span> <strong>Occurrence (1-10):</strong> How frequently does the cause occur?
                                        <br/><span className="text-muted">10 = Inevitable/Every Shift. 1 = Remote/History of zero failures.</span>
                                    </li>
                                    <li className="mb-2">
                                        <span className="badge bg-success me-1">D</span> <strong>Detection (1-10):</strong> Will we catch it <em>before</em> it ships?
                                        <br/><span className="text-muted">10 = Cannot detect. 1 = Error-proofed by System (JobQ prevents it).</span>
                                    </li>
                                </ul>
                            </div>
                            <div className="col-md-4 border-end">
                                <h6 className="fw-bold text-decoration-underline">Definitions & Logic</h6>
                                <ul className="list-unstyled small">
                                    <li className="mb-2"><strong>Failure Mode:</strong> The specific way the process fails (e.g., "Burrs", "Oversize Hole").</li>
                                    <li className="mb-2"><strong>Effect:</strong> What happens to the customer? (e.g., "Cannot Assemble", "Injury").</li>
                                    <li className="mb-2"><strong>Controls:</strong> How we prevent or detect the failure.</li>
                                    <li className="mb-2">
                                        <strong>RPN (Risk Priority Number):</strong> <span className="fw-bold">S × O × D</span>. 
                                        <br/>High RPNs ({'>'}100) require corrective action.
                                    </li>
                                </ul>
                            </div>
                            <div className="col-md-4">
                                <h6 className="fw-bold text-decoration-underline">Classifications</h6>
                                <div className="small">
                                    <div className="mb-2">
                                        <span className="badge bg-danger">Critical</span> Safety or Regulatory issues. Requires special controls (e.g., inverted triangle ∇).
                                    </div>
                                    <div className="mb-2">
                                        <span className="badge bg-warning text-dark">Major</span> Functional failure. Product likely unusable or fitment fails.
                                    </div>
                                    <div className="mb-2">
                                        <span className="badge bg-info text-dark">Key</span> Special Characteristic. Important for fit/form but not safety.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ADD / EDIT FORM */}
            {showForm && (
                <div className={`card mb-4 p-3 border shadow-sm ${editingId ? 'border-warning bg-light' : 'bg-white'}`}>
                    <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                        <h5 className="card-title mb-0">
                            {editingId ? `Edit Failure Mode (ID: ${editingId})` : "Add New Failure Mode"}
                        </h5>
                        {editingId && <span className="badge bg-warning text-dark">Editing Mode</span>}
                    </div>
                    
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label fw-bold">Process Step</label>
                                <select className="form-select" value={formData.process_name} onChange={e => setFormData({...formData, process_name: e.target.value})}>
                                    {PROCESS_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Process Function</label>
                                <input type="text" className="form-control" placeholder="e.g. Cut profile to dimensions" value={formData.process_function} onChange={e => setFormData({...formData, process_function: e.target.value})} required />
                                <div className="form-text">What is this step <em>supposed</em> to do?</div>
                            </div>
                            <div className="col-md-5">
                                <label className="form-label fw-bold">Potential Failure Mode</label>
                                <input type="text" className="form-control" placeholder="e.g. Burrs, Dimensions out of spec" value={formData.failure_mode} onChange={e => setFormData({...formData, failure_mode: e.target.value})} required />
                                <div className="form-text">How does it fail?</div>
                            </div>
                            
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Effect of Failure</label>
                                <textarea className="form-control" rows="2" placeholder="e.g. Assembly interference, Injury" value={formData.effect_of_failure} onChange={e => setFormData({...formData, effect_of_failure: e.target.value})} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Potential Cause</label>
                                <textarea className="form-control" rows="2" placeholder="e.g. Dull tool, Wrong program" value={formData.potential_cause} onChange={e => setFormData({...formData, potential_cause: e.target.value})} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Current Controls</label>
                                <textarea className="form-control" rows="2" placeholder="e.g. JobQ Validation, Calipers" value={formData.current_controls} onChange={e => setFormData({...formData, current_controls: e.target.value})} />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label fw-bold text-danger">Severity (S)</label>
                                <input type="number" min="1" max="10" className="form-control" value={formData.severity} onChange={e => setFormData({...formData, severity: parseInt(e.target.value)})} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-bold text-warning">Occurrence (O)</label>
                                <input type="number" min="1" max="10" className="form-control" value={formData.occurrence} onChange={e => setFormData({...formData, occurrence: parseInt(e.target.value)})} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-bold text-success">Detection (D)</label>
                                <input type="number" min="1" max="10" className="form-control" value={formData.detection} onChange={e => setFormData({...formData, detection: parseInt(e.target.value)})} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-bold">Classification</label>
                                <select className="form-select" value={formData.classification} onChange={e => setFormData({...formData, classification: e.target.value})}>
                                    <option value="">-- None --</option>
                                    <option value="Critical">Critical (Safety)</option>
                                    <option value="Major">Major (Function)</option>
                                    <option value="Key">Key Characteristic</option>
                                    <option value="Significant">Significant</option>
                                </select>
                            </div>
                            <div className="col-12 text-end">
                                <button type="button" className="btn btn-secondary me-2" onClick={resetForm}>Cancel</button>
                                <button type="submit" className={`btn ${editingId ? 'btn-warning' : 'btn-success'}`}>
                                    {editingId ? "Update Template" : "Save to Library"}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}

            {/* FILTER */}
            <div className="card mb-3">
                <div className="card-body py-2 d-flex align-items-center">
                    <label className="me-2 fw-bold">Filter Library by Process:</label>
                    <select className="form-select d-inline-block w-auto" value={filterProcess} onChange={(e) => setFilterProcess(e.target.value)}>
                        <option value="">All Processes</option>
                        {PROCESS_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <span className="ms-auto text-muted small">
                        Showing {templates.length} templates
                    </span>
                </div>
            </div>

            {/* TABLE */}
            <div className="table-responsive">
                <table className="table table-striped table-hover table-bordered align-middle">
                    <thead className="table-dark text-center">
                        <tr>
                            <th style={{width: '10%'}}>Process</th>
                            <th style={{width: '15%'}}>Failure Mode</th>
                            <th style={{width: '15%'}}>Effect</th>
                            <th style={{width: '5%'}} title="Severity">S</th>
                            <th style={{width: '15%'}}>Cause</th>
                            <th style={{width: '5%'}} title="Occurrence">O</th>
                            <th style={{width: '20%'}}>Controls</th>
                            <th style={{width: '5%'}} title="Detection">D</th>
                            <th style={{width: '5%'}} title="Risk Priority Number">RPN</th>
                            <th style={{width: '5%'}}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? <tr><td colSpan="10" className="text-center p-4">Loading Library...</td></tr> : 
                        templates.map(t => (
                            <tr key={t.pfmea_id} className={editingId === t.pfmea_id ? "table-warning" : ""}>
                                <td className="fw-bold">{t.process_name}</td>
                                <td>
                                    <div>{t.failure_mode}</div>
                                    {t.classification && <span className="badge bg-secondary" style={{fontSize: '0.65em'}}>{t.classification}</span>}
                                </td>
                                <td><small>{t.effect_of_failure}</small></td>
                                <td className="text-center fw-bold">{t.severity}</td>
                                <td><small>{t.potential_cause}</small></td>
                                <td className="text-center">{t.occurrence}</td>
                                <td>
                                    {t.current_controls && t.current_controls.includes("JobQ") ? (
                                        <span className="badge bg-info text-dark me-1 mb-1">
                                            <i className="bi bi-cpu"></i> System
                                        </span>
                                    ) : (
                                        <span className="badge bg-light text-dark border me-1 mb-1">
                                            <i className="bi bi-eye"></i> Admin
                                        </span>
                                    )}
                                    <small className="d-block">{t.current_controls}</small>
                                </td>
                                <td className="text-center">{t.detection}</td>
                                <td className={`text-center ${getRpnClass(t.rpn)}`}>{t.rpn}</td>
                                <td className="text-center">
                                    <div className="btn-group btn-group-sm">
                                        <button className="btn btn-outline-primary" onClick={() => handleEdit(t)} title="Edit Template">
                                            <i className="bi bi-pencil"></i>
                                        </button>
                                        <button className="btn btn-outline-danger" onClick={() => handleDelete(t.pfmea_id)} title="Delete Template">
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {templates.length === 0 && !loading && (
                            <tr><td colSpan="10" className="text-center text-muted">No templates found. Add some or change filters.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}